import type { CountryCode } from '@hotjar/country-lib';
import { mapCollectionDtoToModel } from '@hotjar/webapp/domains/collections/mapper';
import { tokenModelToTokenDto } from '@hotjar/comments/domains/mapper';
import { mapLabelDtoToModel, mapLabelIdsModelToDto } from '@hotjar/labels';
import { getCountryNameFromCode } from '@hotjar/country-lib';

import type { PaginatedResponse as PaginatedResponseDto } from '../common/dto';
import {
  mapStringToDeviceType,
  mapGenericHighlightDtoToModel,
  mapHighlightContentStatus,
  mapPaginatedResponse,
} from '../common/mapper';
import type { PaginatedResponse as PaginatedResponseModel } from '../common/model';

import type {
  RecordingHighlight as RecordingHighlightDto,
  RecordingHighlightCreateData as RecordingHighlightCreateDataDto,
  RecordingOnlyHighlightsListItem as RecordingOnlyHighlightsListItemDto,
  RecordingHighlightStatus as RecordingHighlightStatusDto,
} from './dto';
import type {
  BrowserName,
  OperatingSystemFamily,
  RecordingHighlight as RecordingHighlightModel,
  RecordingHighlightCreateData as RecordingHighlightCreateDataModel,
  RecordingOnlyHighlightsListItem as RecordingOnlyHighlightsListItemModel,
  RecordingHighlightStatus as RecordingHighlightStatusModel,
} from './model';

function mapStringToBrowserName(browserName: string | null): BrowserName {
  const lowerCaseBrowserName = (browserName ?? '').toLowerCase();

  // TODO: make sure these are real values
  switch (lowerCaseBrowserName) {
    case 'chrome':
      return 'chrome';
    case 'chromium':
      return 'chromium';
    case 'edge':
      return 'edge';
    case 'firefox':
      return 'firefox';
    case 'opera':
      return 'opera';
    case 'safari':
      return 'safari';
    case 'ie':
      return 'ie';
    default:
      return 'unknown';
  }
}

function mapStringToOperatingSystemFamily(osFamily: string | null): OperatingSystemFamily {
  const lowerCaseOsFamily = (osFamily ?? '').toLowerCase();

  // TODO: make sure these are real values
  switch (lowerCaseOsFamily) {
    case 'windows':
      return 'windows';
    case 'linux':
      return 'linux';
    case 'macos':
      return 'macos';
    case 'ios':
      return 'ios';
    case 'android':
      return 'android';
    default:
      return 'unknown';
  }
}

function mapVisitedUrlsToPagePath(visitedUrls: Array<string> | null): string {
  if (visitedUrls === null || visitedUrls.length === 0) {
    return 'unknown';
  }

  try {
    const parsedUrl = new URL(visitedUrls[0]);
    return parsedUrl.pathname;
  } catch {
    return 'unknown';
  }
}

function mapHjIdToUserId(hjId: string): string {
  return hjId.split('-')[0];
}

function mapScreenResolution(resolution: [number, number] | null): [number, number] {
  if (resolution === null) {
    return [0, 0];
  }

  const [width = 0, height = 0] = resolution;

  return [width, height];
}

export function mapHighlightDtoToRecordingHighlightModel(
  dto: RecordingHighlightDto,
): RecordingHighlightModel {
  return {
    ...mapGenericHighlightDtoToModel(dto),
    metadata: {
      browserName: mapStringToBrowserName(dto.metadata.browser_name),
      clip: {
        duration: dto.metadata.highlight_duration,
        end: dto.metadata.highlight_end_time,
        start: dto.metadata.highlight_start_time,
      },
      countryCode: dto.metadata.country_code
        ? (dto.metadata.country_code as CountryCode)
        : 'unknown',
      countryName: dto.metadata.country_code
        ? getCountryNameFromCode(dto.metadata.country_code.toUpperCase() as CountryCode)
        : 'unknown',
      deviceType: mapStringToDeviceType(dto.metadata.device_type),
      hjId: dto.metadata.hj_id,
      osFamily: mapStringToOperatingSystemFamily(dto.metadata.os_family),
      osName: dto.metadata.os_name ?? 'unknown',
      pagePath: mapVisitedUrlsToPagePath(dto.metadata.visited_urls),
      recordingCreationEpochMs: dto.metadata.recording_created ?? 0,
      recordingDurationEpochMs: dto.metadata.recording_duration,
      referrer: dto.metadata.referrer,
      screenResolution: mapScreenResolution(dto.metadata.screen_resolution),
      tags: dto.metadata.tags,
      time: dto.metadata.highlight_time,
      userId: mapHjIdToUserId(dto.metadata.hj_id),
      videoUrl: dto.metadata.video_url,
      visitedUrls: dto.metadata.visited_urls,
    },
    type: 'recording',
  };
}

export function mapRecordingHighlightCreateDataModelToDto(
  model: RecordingHighlightCreateDataModel,
): RecordingHighlightCreateDataDto {
  return {
    highlight_time: model.highlightTime,
    labels: mapLabelIdsModelToDto(model.labels),
    recording_id: model.recordingId,
    ui_position: model.uiPosition,
    ...(model?.comment && { comment: tokenModelToTokenDto(model.comment) }),
    ...(model?.labels.length > 0 && { labels: model.labels.map((label) => Number(label)) }),
    ...('startTime' in model && { start_time: model.startTime }),
    ...('endTime' in model && { end_time: model.endTime }),
  };
}

export function mapRecordingOnlyHighlightsListItemDtoToModel(
  dto: RecordingOnlyHighlightsListItemDto,
): RecordingOnlyHighlightsListItemModel {
  return {
    author: {
      avatar: dto.user_picture_url,
      id: String(dto.user_id),
      name: dto.user_name,
    },
    collections: dto.collections.map(mapCollectionDtoToModel),
    commentCount: dto.comment_count || 0,
    commentId: dto.comment_id ? String(dto.comment_id) : null,
    contentStatus: mapHighlightContentStatus(dto.status),
    createdDate: new Date(dto.created * 1000),
    id: String(dto.id),
    labels: dto.labels.map(mapLabelDtoToModel),
    solved: dto.solved,
    metadata: {
      clip: {
        duration: dto.metadata.highlight_duration,
        end: dto.metadata.highlight_end_time,
        marker: dto.metadata.highlight_time,
        start: dto.metadata.highlight_start_time,
      },
      videoUrl: dto.metadata.video_url,
    },
  };
}

export function mapRecordingOnlyHighlightsDtoToModel(
  data: PaginatedResponseDto<RecordingOnlyHighlightsListItemDto>,
): PaginatedResponseModel<RecordingOnlyHighlightsListItemModel> {
  return mapPaginatedResponse(mapRecordingOnlyHighlightsListItemDtoToModel, data);
}

export function mapRecordingHighlightStatusDtoToModel(
  dto: RecordingHighlightStatusDto,
): RecordingHighlightStatusModel {
  const contentStatus = mapHighlightContentStatus(dto.status);

  if (contentStatus === 'done') {
    return {
      contentStatus,
      thumbnailUrl: dto.thumbnail_url as string,
      videoUrl: dto.video_url as string,
    };
  }

  return {
    contentStatus,
    thumbnailUrl: null,
    videoUrl: null,
  };
}
